import React from 'react';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';

import { fetchTutorial, saveTutorial } from 'utils/api/tutorials';

const fields = { completed: 'completedAt', dismissed: 'dismissedAt' };
const EXTRA_TOUCH_TOUR_TARGET = '[data-tour-id="extra-touch-action"]';

// The parent mounts this hook only for an eligible, non-impersonated account.
const useExtraTouchOnboarding = ({ onAddOneNow }) => {
  const [state, setState] = React.useState(null);
  const [failedStatuses, setFailedStatuses] = React.useState([]);
  const [tourStopped, setTourStopped] = React.useState(false);
  const requests = React.useRef(null);
  const submitted = React.useRef(new Set());
  const finished = React.useRef(false);

  React.useEffect(() => {
    const controller = new AbortController();
    requests.current = controller;
    fetchTutorial('extra-touch', controller.signal).then((saved) => {
      if (!controller.signal.aborted && saved.version === 1) setState(saved);
    }).catch(() => {
      // An unavailable account state must not trigger an unsolicited tutorial.
    });
    return () => controller.abort();
  }, []);

  const save = React.useCallback(async (status) => {
    const signal = requests.current?.signal;
    if (!signal || signal.aborted || submitted.current.has(status)) return;
    submitted.current.add(status);
    const field = fields[status];
    // Advance locally while saving; failed writes remain available for explicit retry.
    setState((current) => ({ ...current, [field]: current?.[field] || new Date().toISOString() }));
    setFailedStatuses((current) => current.filter((entry) => entry !== status));
    try {
      const saved = await saveTutorial('extra-touch', status, signal);
      if (signal.aborted) return;
      setState((current) => ({ ...current, [field]: saved[field] }));
    } catch (error) {
      if (signal.aborted) return;
      submitted.current.delete(status);
      setFailedStatuses((current) => [...new Set([...current, status])]);
    }
  }, []);

  const finish = React.useCallback((status) => {
    if (finished.current || requests.current?.signal.aborted) return;
    finished.current = true;
    save(status);
  }, [save]);

  const joyrideSteps = React.useMemo(() => ([{
    target: EXTRA_TOUCH_TOUR_TARGET,
    content: 'Use this to add a one-off touch to this campaign.',
    placement: 'bottom-start',
    skipBeacon: true,
    blockTargetInteraction: false,
    overlayClickAction: 'close',
    data: {
      onComplete: () => finish('completed'),
      onDismiss: () => finish('dismissed'),
      onAddOneNow: () => {
        if (!requests.current?.signal.aborted) onAddOneNow();
      },
    },
  }]), [finish, onAddOneNow]);

  const handleJoyrideCallback = React.useCallback(({ action, status, type }) => {
    if (type.startsWith('error:')) {
      // A missing target is not evidence that the user saw the tutorial.
      finished.current = true;
      setTourStopped(true);
    } else if (action === ACTIONS.CLOSE || (type === EVENTS.TOUR_END && status === STATUS.SKIPPED)) {
      finish('dismissed');
    } else if (type === EVENTS.TOUR_END && status === STATUS.FINISHED) {
      finish('completed');
    }
  }, [finish]);

  const unseen = state && !state.completedAt && !state.dismissedAt;
  return {
    shouldAutoStartTour: !!unseen && !tourStopped,
    handleJoyrideCallback,
    joyrideSteps,
    hasSaveError: failedStatuses.length > 0,
    retrySave: () => failedStatuses.forEach((status) => save(status)),
  };
};

export default useExtraTouchOnboarding;
